import
{ ApplicationRef
, EnvironmentProviders
, Injectable
, declareExperimentalWebMcpTool
, inject
, makeEnvironmentProviders
, provideEnvironmentInitializer
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of } from 'rxjs';
import { filter, map, timeout } from 'rxjs/operators';
import { ErrorStateService } from '../core/error-state.service';
import { GmrDataService, toDateInputValue } from '../core/gmr-data.service';
import { GmrFunction, GmrFunctionParameter, GmrLovRow } from '../core/gmr.models';
import { HashStateService } from '../core/hash-state.service';
import { LoadingService } from '../core/loading.service';
import { UiStateService } from '../core/ui-state.service';
import { DIVISIONS_MULTI_LOV_PRESET, isParameterMissing } from '../parameter-form/parameter-validation';
import { buildSearchHaystack } from '../shared/function-search';

//
// WebMCP tools: the report catalogue and the parameter wizard, exposed to an in-browser AI agent as
// named, typed operations instead of as pixels to be clicked.
//
// Five tools, four of them read-only. What is deliberately ABSENT is a `download_report`, and the
// reason is not caution for its own sake:
//
//   - A download is a native <form> submit into a hidden iframe (parameter-form.component.html). Its
//     result is a file the browser saves, not a value a tool can return. An agent that triggered one
//     would receive nothing it could reason about, so the tool would spend the user's Exact Online
//     API budget to produce output the caller cannot even see.
//   - Chrome's consent mechanism for agent-initiated actions (requestUserInteraction()) is still
//     unimplemented, and Angular's ToolDescriptor exposes neither `annotations` (readOnlyHint,
//     untrustedContentHint) nor `exposedTo`. There is therefore no way, today, to mark a tool as
//     state-changing or to restrict which origin may call it.
//
// `prepare_report` is the answer to that: it fills the form in and hands back the bookmark URL the
// application already produces for sharing, and the user presses Download. The agent does all the
// work that is safe to automate and stops exactly where a human decision starts.
//
// Registration is a no-op outside Chrome: declareExperimentalWebMcpTool() checks for
// document.modelContext / navigator.modelContext and returns without touching anything when neither
// exists, so Firefox, Safari and a Chrome without the origin trial are unaffected. Verified against
// @angular/core 22.1.0.
//

//
// Chrome truncates tool output that runs past roughly 1.5K characters. Truncation by the platform is
// invisible to the agent, so every list here is cut by joinWithinBudget() instead, which says out
// loud that it cut something.
//
const TOOL_OUTPUT_BUDGET = 1500;

//
// How long a tool waits for the report catalogue before giving up. Generous, because the wait covers
// an OAuth round trip on a cold session; bounded, because the application has a documented failure
// mode in which the catalogue never arrives at all (KNOWN_ISSUES) and a promise that never settles
// would leave the agent hanging with no message at all.
//
const CATALOGUE_TIMEOUT_MS = 30_000;

//
// One promise per application, resolved when the catalogue settles.
//
// Every tool has to wait for it: an agent can call within milliseconds of the page appearing, long
// before auth and the gmr-functions preset have returned, and a tool answering "0 reports" during
// that window is not merely unhelpful - it is a confident, wrong answer that the agent will repeat to
// the user as fact.
//
// A service rather than a helper called per invocation, because toObservable() creates an effect
// bound to the injector's lifetime: built once here it costs one effect for the whole session instead
// of one per agent call.
//
@Injectable({ providedIn: 'root' })
class CatalogueGate
{
  private readonly loading = inject(LoadingService);

  readonly ready: Promise<boolean> = firstValueFrom
  ( toObservable(this.loading.awaitingCatalog).pipe
    ( filter(awaiting => !awaiting)
    , map(() => true)
    , timeout({ first: CATALOGUE_TIMEOUT_MS, with: () => of(false) })
    )
  , { defaultValue: false }
  );
}

//
// Refuses the four report tools when the catalogue is not actually usable, and says which of the two
// reasons applies.
//
// The distinction is the whole point, and it was learned the hard way on 2026-08-14: waiting for
// CatalogueGate alone is NOT enough. GmrDataService clears awaitingCatalog on the failure path as well
// as on the success path - deliberately, so the loading overlay gives way to the error modal - so a
// tool that only awaited the gate carried on and reported "0 reports in the catalogue" while an error
// modal was on screen saying the response could not be read. That is not a small inaccuracy: an empty
// catalogue and a broken one look identical from the outside, and the agent presents the first as a
// fact about the user's licence.
//
// Services are passed in rather than injected here. inject() is only valid during the SYNCHRONOUS part
// of a tool's execute() - Angular runs it through runInInjectionContext, and that context is gone the
// moment the first await suspends - so every injection has to happen at the top of execute(), before
// this is awaited.
//
async function catalogueProblem
( gate: CatalogueGate
, data: GmrDataService
, errorState: ErrorStateService
): Promise<string | null>
{
  if (!await gate.ready)
  {
    return 'The Get My Report catalogue has still not arrived. The application is stuck waiting for '
      + 'data; nothing can be reported about this user\'s reports. Ask the user to reload the page.';
  }

  if (data.myFunctions().length > 0) return null;

  const error = errorState.errorModal();

  return error.visible
    ? `The Get My Report catalogue failed to load. The application is showing: ${error.title} - ${error.message}`
    : 'The Get My Report catalogue is empty: the user is not signed in to Exact Online, or this '
      + 'subscription grants no reports at all.';
}

//
// Not Angular's own provideExperimentalWebMcpTools([...]), and not by preference.
//
// That function declares a SINGLE `const InputSchema` type parameter for the whole array, so five
// tools with five different schemas collapse into one inferred type and every `execute` argument
// after the first arrives as `unknown` - TS2345 and TS18046 at compile time, against @angular/core
// 22.1.0. Each declareExperimentalWebMcpTool() call gets its own instantiation and therefore its own
// correctly typed arguments. The runtime behaviour is identical: the provider does no more than call
// this same function once per tool from an environment initialiser.
//
// One function per tool for the same reason. A `const` type parameter only tightens inference for a
// literal written at the call site, so hoisting the descriptors into shared constants would widen
// `required: ['preset']` back to `string[]` and lose the argument types again.
//
export function provideGmrWebMcpTools(): EnvironmentProviders
{
  return makeEnvironmentProviders
  ([ provideEnvironmentInitializer(() =>
     {
       declareListReports();
       declareDescribeReport();
       declareOpenReport();
       declareGetAccountStatus();
       declarePrepareReport();
     })
  ]);
}

function declareListReports(): void
{
  void declareExperimentalWebMcpTool
  ({ name: 'list_reports'
   , description:
       'Search the Get My Report catalogue of Exact Online reports this user may run. Matches the '
     + 'same text the on-screen search box matches: report title, explanation, module and preset '
     + 'name, in the interface language. Returns one line per report, starting with the preset name '
     + 'the other tools take as input. Read-only.'
   , inputSchema:
     { type: 'object'
     , properties:
       { query:
         { type: 'string'
         , description: 'Search term. An empty string lists the catalogue from the beginning.'
         }
       }
     , required: ['query']
     }
   , execute: async ({ query }) =>
     {
       const data = inject(GmrDataService);
       const translate = inject(TranslateService);
       const gate = inject(CatalogueGate);
       const errorState = inject(ErrorStateService);

       const problem = await catalogueProblem(gate, data, errorState);
       if (problem) return problem;

       const term = query.trim().toLowerCase();
       const matches = term.length === 0
         ? data.myFunctions()
         : data.myFunctions().filter(fn => buildSearchHaystack(fn, key => translate.instant(key)).includes(term));

       //
       // Only reachable for a non-empty query: catalogueProblem() has already established that the
       // catalogue holds at least one report, so an empty query cannot land here. Saying "call this
       // with an empty query" unconditionally would otherwise answer an empty query with advice to
       // repeat it, which is what the first version of this tool did.
       //
       if (matches.length === 0)
       {
         return `None of the ${data.myFunctions().length} reports available to this user matches `
           + `"${query}". Call list_reports with an empty query to see the whole catalogue.`;
       }

       return joinWithinBudget
       ( `${matches.length} report(s) match "${query}":`
       , matches.map(fn =>
         {
           const module = fn.can_description_i18n ?? translate.instant(fn.can_description);
           const parameters = fn.cfrstat_cnt === 0 ? 'no parameters' : `${fn.cfrstat_cnt} parameter(s)`;
           return `${fn.cfn_preset_name} - ${translate.instant(fn.cfn_description)} [${module}, ${parameters}]`;
         })
       );
     }
   });
}

function declareDescribeReport(): void
{
  void declareExperimentalWebMcpTool
  ({ name: 'describe_report'
   , description:
       'List the parameters of one Get My Report report: code, type, whether it is required, its '
     + 'default and whether it is picked from a list of values. Use the parameter codes it returns as '
     + 'input for prepare_report. Read-only: it does not open the report and issues no request.'
   , inputSchema:
     { type: 'object'
     , properties:
       { preset:
         { type: 'string'
         , description: 'Preset name of the report, as returned by list_reports.'
         }
       }
     , required: ['preset']
     }
   , execute: async ({ preset }) =>
     {
       const data = inject(GmrDataService);
       const translate = inject(TranslateService);
       const gate = inject(CatalogueGate);
       const errorState = inject(ErrorStateService);

       const problem = await catalogueProblem(gate, data, errorState);
       if (problem) return problem;

       const fn = findFunction(data, preset);
       if (!fn) return unknownReport(data, preset);

       const title = translate.instant(fn.cfn_description);

       //
       // Read straight from myParameters() rather than selecting the report first. Selecting has side
       // effects a description has no business causing: it loads every list of values, fires the
       // "downloaded report" analytics event, and - for a report without parameters - navigates the
       // whole page to the download.
       //
       const parameters = data.myParameters().filter(parameter => parameter.cfn_code === fn.cfn_code);

       if (parameters.length === 0)
       {
         return `${fn.cfn_preset_name} - ${title}\nThis report has no parameters. It cannot be prepared or `
           + 'opened by an agent: selecting it downloads it immediately, which is left to the user.';
       }

       return joinWithinBudget
       ( `${fn.cfn_preset_name} - ${title}\n${parameters.length} parameter(s):`
       , parameters.map(parameter =>
         {
           const facts = [parameter.data_ind_html_control];

           if (parameter.cfr_lov_preset === DIVISIONS_MULTI_LOV_PRESET)
           {
             facts.push('multi-select of Exact Online companies, at least one required');
           }
           else if (parameter.cfr_lov_preset != null)
           {
             facts.push('value picked from a list');
           }

           if (parameter.cfr_required_flag === true) facts.push('required');
           if (parameter.cfr_depends_on != null) facts.push(`depends on ${parameter.cfr_depends_on}`);
           if (parameter.cfr_default_value != null) facts.push(`default ${parameter.cfr_default_value}`);

           return `${parameter.cfr_code} - ${translate.instant(parameter.cfr_description)} (${facts.join(', ')})`;
         })
       );
     }
   });
}

function declareOpenReport(): void
{
  void declareExperimentalWebMcpTool
  ({ name: 'open_report'
   , description:
       'Open a Get My Report report in the wizard so its parameter form is on screen, and put its '
     + 'preset in the address bar so the user can bookmark it or go back. Downloads nothing and spends '
     + 'no Exact Online API budget. Refuses reports without parameters, because selecting one of those '
     + 'downloads it immediately.'
   , inputSchema:
     { type: 'object'
     , properties:
       { preset:
         { type: 'string'
         , description: 'Preset name of the report, as returned by list_reports.'
         }
       }
     , required: ['preset']
     }
   , execute: async ({ preset }) =>
     {
       const data = inject(GmrDataService);
       const ui = inject(UiStateService);
       const appRef = inject(ApplicationRef);
       const translate = inject(TranslateService);
       const gate = inject(CatalogueGate);
       const errorState = inject(ErrorStateService);

       const problem = await catalogueProblem(gate, data, errorState);
       if (problem) return problem;

       const fn = findFunction(data, preset);
       if (!fn) return unknownReport(data, preset);
       if (fn.cfrstat_cnt === 0) return refusesParameterlessReport(fn);

       await openReport(fn, data, ui, appRef);

       const opened = `Opened ${fn.cfn_preset_name} - ${translate.instant(fn.cfn_description)} at ${window.location.href}.`;
       const mismatch = parameterCountMismatch(fn, data);

       if (mismatch) return `${opened} ${mismatch}`;

       return `${opened} ${data.selectedFunctionParameters().length} parameter(s) are on screen. `
         + 'Use describe_report for what they mean and prepare_report to fill them in.';
     }
   });
}

function declareGetAccountStatus(): void
{
  void declareExperimentalWebMcpTool
  ({ name: 'get_account_status'
   , description:
       'Report the state of this Get My Report session: company, interface language, size of the '
     + 'report catalogue, which report is open, how many Exact Online companies are available and why '
     + 'the others are not, and the remaining Exact Online API call budget. Read-only. Explains why a '
     + 'report may return nothing or may be refused.'
   , inputSchema: { type: 'object', properties: {} }
   , execute: async () =>
     {
       const data = inject(GmrDataService);
       const ui = inject(UiStateService);
       const gate = inject(CatalogueGate);
       const errorState = inject(ErrorStateService);

       //
       // The one tool that reports a broken catalogue instead of refusing over it. "Why does this
       // application show me nothing?" is precisely the question it exists to answer, and answering
       // it with the same refusal the other four give would leave the agent with no way to find out.
       //
       const problem = await catalogueProblem(gate, data, errorState);

       const info = data.myInfo();
       const stats = data.unavailableStats();
       const selected = data.selectedFunction();
       const lines: string[] = [];

       if (problem) lines.push(problem);

       //
       // Company, language and API budget, but deliberately not usr_name or usr_email_address. The
       // agent is being told about the session, not about the person holding it; nothing any of these
       // five tools does needs a name or an address, and what is never handed over cannot be carried
       // off to a model backend along with the rest of the conversation.
       //
       if (info)
       {
         lines.push(`Company: ${info.usr_company_name}`);
         lines.push(`Profile language: ${info.usr_language}`);

         //
         // Exact Online's own API call budget, not a Get My Report report quota - the settings panel
         // labels these "API Call Limits" and "API Remaining Calls". Reported as the raw min..max pair
         // the backend sends, which spans the companies this user can reach: the report that runs
         // against the tightest company is the one that fails first, so the minimum is the number that
         // decides, and collapsing the pair would hide it.
         //
         if (info.daily_remaining_min != null || info.daily_remaining_max != null)
         {
           lines.push
           ( `Exact Online API calls remaining today: ${info.daily_remaining_min ?? '?'}..${info.daily_remaining_max ?? '?'} `
           + `of a daily limit of ${info.daily_limit_min ?? '?'}..${info.daily_limit_max ?? '?'} (range across companies).`
           );
         }
       }

       lines.push(`Reports in the catalogue: ${data.myFunctions().length}`);
       lines.push(`Wizard step: ${ui.step()}`);
       lines.push(selected ? `Report open: ${selected.cfn_preset_name}` : 'Report open: none');

       if (stats)
       {
         lines.push(`Exact Online companies available: ${stats.available_cnt}`);

         const unavailable =
         [ { count: stats.inactive_cnt, reason: 'inactive' }
         , { count: stats.outside_service_area_cnt, reason: 'outside the service area' }
         , { count: stats.unknown_cnt, reason: 'unavailable for an unknown reason' }
         , { count: stats.compliance_cnt, reason: 'not compliant with policy' }
         ].filter(entry => entry.count > 0);

         for (const entry of unavailable)
         {
           lines.push(`  ${entry.count} unavailable: ${entry.reason}`);
         }
       }

       return joinWithinBudget('Get My Report session status:', lines);
     }
   });
}

function declarePrepareReport(): void
{
  void declareExperimentalWebMcpTool
  ({ name: 'prepare_report'
   , description:
       'Open a Get My Report report, fill in its parameters and return the bookmark URL that downloads '
     + 'it, together with whatever is still missing. Deliberately does NOT download and spends no '
     + 'Exact Online API budget: the user presses Download themselves. Get the parameter codes from '
     + 'describe_report first; for the companies field pass a comma-separated list of division codes.'
   , inputSchema:
     { type: 'object'
     , properties:
       { preset:
         { type: 'string'
         , description: 'Preset name of the report, as returned by list_reports.'
         }
       , parameters:
         { type: 'array'
         , description: 'Values to fill in. Parameters left out keep the default the report ships with.'
         , items:
           { type: 'object'
           , properties:
             { code:
               { type: 'string'
               , description: 'Parameter code exactly as describe_report returned it.'
               }
             , value:
               { type: 'string'
               , description:
                   'Value as text. Dates as yyyy-mm-dd, checkboxes as "true" or "false", the companies '
                 + 'field as comma-separated division codes.'
               }
             }
           , required: ['code', 'value']
           }
         }
       }
     , required: ['preset', 'parameters']
     }
   , execute: async ({ preset, parameters }) =>
     {
       const data = inject(GmrDataService);
       const ui = inject(UiStateService);
       const appRef = inject(ApplicationRef);
       const hashState = inject(HashStateService);
       const translate = inject(TranslateService);
       const gate = inject(CatalogueGate);
       const errorState = inject(ErrorStateService);

       const problem = await catalogueProblem(gate, data, errorState);
       if (problem) return problem;

       const fn = findFunction(data, preset);
       if (!fn) return unknownReport(data, preset);
       if (fn.cfrstat_cnt === 0) return refusesParameterlessReport(fn);

       await openReport(fn, data, ui, appRef);

       const requested = new Map(parameters.map(entry => [entry.code, entry.value] as const));
       const applied: string[] = [];
       const unrecognisedValues: string[] = [];

       //
       // Walk the form's own parameter order rather than the order the agent supplied, and replace
       // each changed entry with a CLONE instead of writing into it.
       //
       // The clone is what makes the change visible: LovSelectComponent keeps the hidden input that
       // actually carries a list-of-values field into the submit in sync from ngOnChanges, which only
       // fires when the @Input reference changes. An in-place write would update the model, leave that
       // mirror stale, and produce a bookmark that silently disagrees with the screen. The same
       // clone-and-reassign shape is what restoreDivisionCodes() and selectAllDivisions() already use,
       // for exactly this reason.
       //
       const next = data.selectedFunctionParameters().map(parameter =>
       {
         const value = requested.get(parameter.cfr_code);
         if (value === undefined) return parameter;

         requested.delete(parameter.cfr_code);
         applied.push(parameter.cfr_code);

         const unknown = unknownLovValues(data, parameter, value);
         if (unknown.length > 0)
         {
           unrecognisedValues.push(`${parameter.cfr_code}=${unknown.join(',')}`);
         }

         return applyValue(parameter, value);
       });

       data.selectedFunctionParameters.set(next);

       //
       // Dependent lists of values are deliberately NOT cascaded here, unlike an on-screen change.
       // GmrDataService.cascadeDependents() resolves its target slot with myParameters().indexOf(), an
       // index into the whole catalogue's parameter list, while myLovPresets is indexed within the
       // selected report - so triggering it writes the fetched rows into the wrong field. That is a
       // pre-existing defect, not one to provoke from here, and it does not affect this tool's output:
       // the bookmark and the submit read the field values, not the dropdown contents.
       //
       await appRef.whenStable();

       const missing = data.selectedFunctionParameters().filter(isParameterMissing);

       //
       // Serialised from the live <form>, through the very function the Download button and the
       // bookmark icon use, rather than rebuilt from the parameter list. A second serialiser could
       // disagree with the real one - over an unchecked checkbox, or over how the companies field is
       // joined - and that failure would surface as a download silently returning the wrong rows,
       // which is the worst shape a reporting bug can take.
       //
       const form = document.querySelector<HTMLFormElement>('form[name="presets"]');
       if (!form)
       {
         return `Opened ${fn.cfn_preset_name}, but its form is not in the page, so no bookmark URL could be `
           + 'produced. That means the wizard is not showing the parameter form; report it as a defect.';
       }

       hashState.writeBookmark(form);

       const lines =
       [ `Report: ${fn.cfn_preset_name} - ${translate.instant(fn.cfn_description)}`
       , `Applied: ${applied.length > 0 ? applied.join(', ') : 'nothing, defaults kept'}`
       , `Ready to download: ${missing.length === 0 ? 'yes' : 'no'}`
       , `Bookmark URL: ${window.location.href}`
       ];

       if (missing.length > 0)
       {
         lines.push(`Still required: ${missing.map(parameter => parameter.cfr_code).join(', ')}`);
       }

       if (requested.size > 0)
       {
         lines.push(`Ignored, no such parameter on this report: ${[...requested.keys()].join(', ')}`);
       }

       if (unrecognisedValues.length > 0)
       {
         lines.push(`Set but absent from the field's list of values, check before downloading: ${unrecognisedValues.join('; ')}`);
       }

       //
       // Reported, not refused. A stale count in the catalogue is the harmless version of this; the
       // harmful version is a parameter that never arrived, in which case "ready to download: yes" is
       // true of the form and false of the report - it would run unfiltered over every row the user
       // can see.
       //
       const mismatch = parameterCountMismatch(fn, data);
       if (mismatch) lines.push(mismatch);

       lines.push
       ( missing.length === 0
           ? 'The form is filled in and valid. Ask the user to press Download, or to open the URL above.'
           : 'Fill in the remaining parameters before the user can download.'
       );

       return joinWithinBudget('', lines);
     }
   });
}

//
// Shows the report and makes the address bar agree with it.
//
// The selection goes through GmrDataService/UiStateService rather than through a hash change alone,
// even though App listens for hashchange and would eventually do the same: hashchange is delivered as
// its own task, so a tool that only wrote the hash would have to guess how long to wait before reading
// back what it had done. Writing the hash afterwards is still necessary - it is what makes the result
// bookmarkable and the Back button meaningful - and it is harmless, because App.applyHash() returns
// immediately when the named report is already the one on screen.
//
async function openReport
( fn: GmrFunction
, data: GmrDataService
, ui: UiStateService
, appRef: ApplicationRef
): Promise<void>
{
  const alreadyOpen = data.selectedFunction()?.cfn_preset_name === fn.cfn_preset_name
    && ui.step() === 'parameterForm';

  if (!alreadyOpen && data.selectFunction(fn))
  {
    ui.goToParameterForm(fn.cfn_preset_name);
  }

  window.location.hash = 'preset=' + fn.cfn_preset_name;

  //
  // Waits for the lists of values selectFunction() just requested as well as for rendering: until they
  // land the fields are empty, so both the "still required" verdict and the serialised form would
  // describe a screen that no longer exists a moment later.
  //
  await appRef.whenStable();
}

//
// Writes one agent-supplied value into a copy of the parameter, in the shape that field expects.
//
function applyValue(parameter: GmrFunctionParameter, value: string): GmrFunctionParameter
{
  if (parameter.cfr_lov_preset === DIVISIONS_MULTI_LOV_PRESET)
  {
    const codes = value.split(',').map(code => code.trim()).filter(code => code.length > 0);
    return { ...parameter, cfr_default_value_arr: codes };
  }

  if (parameter.data_ind_html_control === 'checkbox')
  {
    //
    // Anything not recognisably affirmative is false, matching ParameterFieldComponent, which stores
    // the two literal strings 'true' and 'false' rather than a boolean.
    //
    const checked = ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase());
    return { ...parameter, cfr_default_value: checked ? 'true' : 'false' };
  }

  if (parameter.data_ind_html_control === 'date')
  {
    return { ...parameter, cfr_default_value: toDateInputValue(value) };
  }

  return { ...parameter, cfr_default_value: value };
}

//
// Which of the supplied codes are not in the field's own list of values.
//
// Reported rather than rejected. The list is loaded per field and can legitimately be partial - a
// dependent list narrows to its parent's selection, and a searchable one holds only what was searched
// for - so refusing an unlisted value would block correct input. An agent that invented a division
// code, on the other hand, produces a download of the wrong company's figures with nothing on screen
// saying so, which is why silence is not an option either.
//
function unknownLovValues(data: GmrDataService, parameter: GmrFunctionParameter, value: string): string[]
{
  if (parameter.cfr_lov_preset == null) return [];

  const index = data.selectedFunctionParameters().indexOf(parameter);
  const rows: GmrLovRow[] | undefined = data.myLovPresets()[index];
  if (!rows || rows.length === 0) return [];

  const known = new Set(rows.map(row => String(row.code)));
  const supplied = parameter.cfr_lov_preset === DIVISIONS_MULTI_LOV_PRESET
    ? value.split(',').map(code => code.trim())
    : [value.trim()];

  return supplied.filter(code => code.length > 0 && !known.has(code));
}

//
// Says so when the catalogue's own parameter count disagrees with the parameters that were actually
// delivered for the open report.
//
// The application already detects this - GmrDataService.selectFunction() raises xxgmr112 - but it does
// so through alert(), which an agent driving the page never sees and which no tool result carries. The
// consequence is not cosmetic: a required filter that failed to arrive leaves a form the browser calls
// valid, and the report then runs across everything instead of the intended selection.
//
function parameterCountMismatch(fn: GmrFunction, data: GmrDataService): string | null
{
  const onScreen = data.selectedFunctionParameters().length;
  if (onScreen === fn.cfrstat_cnt) return null;

  return `WARNING: the catalogue says this report has ${fn.cfrstat_cnt} parameter(s), but ${onScreen} `
    + 'were delivered. Its definition is inconsistent, so a download may cover more data than intended. '
    + 'Report it as a defect (xxgmr112) rather than working around it.';
}

function findFunction(data: GmrDataService, preset: string): GmrFunction | undefined
{
  return data.myFunctions().find(fn => fn.cfn_preset_name === preset);
}

//
// A wrong preset name is the likeliest mistake an agent makes, so the refusal carries the material to
// correct it rather than only stating the fact.
//
function unknownReport(data: GmrDataService, preset: string): string
{
  const term = preset.toLowerCase();
  const near = data.myFunctions()
    .filter(fn => fn.cfn_preset_name.toLowerCase().includes(term))
    .map(fn => fn.cfn_preset_name)
    .slice(0, 5);

  const suggestion = near.length > 0
    ? ` Did you mean: ${near.join(', ')}?`
    : ' Use list_reports to find valid preset names.';

  return `No report named "${preset}" is available to this user.${suggestion}`;
}

function refusesParameterlessReport(fn: GmrFunction): string
{
  return `${fn.cfn_preset_name} has no parameters: opening it in this application downloads it straight `
    + 'away and spends Exact Online API budget. That is left to the user. Point them at the report in the '
    + 'list instead of doing it for them.';
}

//
// Joins a header and a list of lines, stopping before Chrome's output limit and saying so.
//
function joinWithinBudget(header: string, items: readonly string[]): string
{
  const lines = header.length > 0 ? [header] : [];
  let length = header.length;

  for (let index = 0; index < items.length; index++)
  {
    //
    // The trailer is measured before the item is admitted, not once the budget is already gone: a list
    // that simply stops reads as a complete answer, and an agent would then tell the user a report
    // does not exist when it was merely cut off.
    //
    const trailer = `... and ${items.length - index} more, not shown. Narrow the search.`;

    if (length + items[index].length + trailer.length + 2 > TOOL_OUTPUT_BUDGET)
    {
      lines.push(trailer);
      break;
    }

    lines.push(items[index]);
    length += items[index].length + 1;
  }

  return lines.join('\n');
}
